import { useState, useEffect } from 'react';
import {
  Box, Flex, Heading, Text, Icon,
} from '@chakra-ui/react';
import { LuCheck } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { usePreferences } from '../../contexts/PreferencesContext';
import { persistWorkspaceId } from '../../auth/workspaceService';
import { currencyForCountry } from '../../utils/onboardingCountries';
import { detectCountry } from '../../utils/homeCurrency';
import { hostName } from '../../utils/hostName';
import useIsPhone from '../../hooks/useIsPhone';
import OwnerAvatar from '../shared/OwnerAvatar';
import RoleChip from './RoleChip';
import GuestRoleStep from './GuestRoleStep';
import GuestFinish from './GuestFinish';
import {
  OnboardingFrame, StageProgress, LanguageField, CountryField, usePressGate,
} from './OnboardingShell';

// The stops that open on a standing line: Welcome says there is nothing to
// set up, and Your role says who chose these permissions and whom to ask for
// more (the mockup's intro above "You're joining as …").
const DESC_KEYS = { welcome: 'guest.welcome.desc', role: 'guest.role.desc' };

// The country we would suggest to someone who has none yet: the same lookup
// the display currency falls back to (time zone, then the browser's region),
// kept only when it is one the list offers, so the field can show it.
function suggestedCountry() {
  const code = detectCountry(null);
  return currencyForCountry(code) ? code : null;
}

/**
 * The invited member's onboarding: Welcome · Your role · Done, on the owner
 * flow's page and shell.
 *
 * Someone who signed up to join another person's space has nothing to set up
 * there, and nothing here may write to it. The active workspace is the host's
 * (the accept page makes it so), and everything workspace-scoped would land in
 * their ledger: `createAccount` (the owner flow's cash account and account
 * step), `createTag`, and `updateCurrency` (the owner flow activates the
 * country's currency). A Viewer would get a 403 besides. So this flow writes
 * only the user's own preferences — language, country, home currency and its
 * own progress keys — and a test holds it to that.
 *
 * It also never stamps the owner flow's keys (`wizardStartedAt`,
 * `currentStepKey`, …). If the host removes them before they finish, the gate
 * ignores the invitation and the owner flow applies; it must then start fresh,
 * not resume a flow this one pretended to begin.
 *
 * See docs/specs/2026-09-23-full-page-onboarding-design.md, "Invited members".
 */
export default function GuestOnboarding({ steps, workspace, role }) {
  const { t } = useTranslation('onboarding');
  const isPhone = useIsPhone();
  const {
    setLanguage, setCountry, country, setHomeCurrency, onboarding,
    setInviteStep, markInviteCompleted,
  } = usePreferences();

  // Resumes from its own key, never the owner's `currentStepKey`, so the two
  // flows cannot resume each other.
  const [step, setStep] = useState(() => Math.max(0, steps.indexOf(onboarding?.inviteStepKey)));
  const key = steps[step];
  const [suggested] = useState(() => (country ? null : suggestedCountry()));
  const [entering, setEntering] = useState(false);

  const name = hostName(workspace) || t('guest.fallbackName');
  const roleLabel = t(`workspaces:roles.${role}`, role);

  // Remember where they are, so leaving and coming back lands on this screen.
  useEffect(() => { setInviteStep(key); }, [key, setInviteStep]);

  const goNext = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const { acceptPress, advancing } = usePressGate(step);
  const handleBack = () => { if (acceptPress()) goBack(); };

  // The owner flow's country handler also activates the currency, which is
  // workspace-scoped: here it would change the host's space. This one keeps
  // to the user's own preferences.
  const chooseCountry = (code) => {
    if (!code) return;
    setCountry(code);
    const cur = currencyForCountry(code);
    if (cur) setHomeCurrency(cur);
  };

  const handleNext = () => {
    if (!acceptPress()) return;
    // Continuing is confirming: the suggestion becomes their country.
    if (key === 'welcome' && !country && suggested) chooseCountry(suggested);
    goNext();
  };

  // Into the host's space, with one page load. `inviteCompletedAt` has to reach
  // the server first: the load re-reads preferences from it, and without the
  // stamp the gate would hold them here again. The write resolves either way;
  // if it failed, they land back on this step with the server's copy, and the
  // button stamps it again. The workspace is made the active one the way
  // `switchWorkspace` does it, but without its reload, which would be a
  // second one — or none, when it is already active and the path must change.
  const enter = async () => {
    if (!acceptPress()) return;
    advancing.current = true;
    setEntering(true);
    await markInviteCompleted();
    persistWorkspaceId(workspace.id);
    // Replacing the entry: onboarding is over, so going back must not land on
    // it. `/` opens Home on the phone.
    window.location.replace(isPhone ? '/' : '/movements');
  };

  const title = t('guest.header.title', { name });
  const subtitle = t('guest.header.subtitle');

  // On the computer: who invited them, on every stop — it is why they are here.
  const header = (
    <Flex align="center" gap="16px" mb={{ base: 6, md: '36px' }}>
      <OwnerAvatar workspace={workspace} size={52} />
      <Box minW={0}>
        <Heading
          as="h1"
          fontSize={{ base: '22px', md: '27px' }}
          fontWeight={700}
          letterSpacing="-0.02em"
          color="redesign.heading"
          m={0}
        >
          {title}
        </Heading>
        <Flex align="center" gap="10px" mt="6px" wrap="wrap">
          <Text fontSize="14px" color="redesign.text.muted" m={0}>{subtitle}</Text>
          <RoleChip role={role} />
        </Flex>
      </Box>
    </Flex>
  );

  // On the phone the full banner is on the first stop only; the role lists
  // need the height, so later stops keep just whose space it is, beside the
  // step counter.
  const phoneTop = (
    <>
      <StageProgress
        steps={steps}
        stepIndex={step}
        aside={step > 0 && (
          <Flex align="center" gap="6px" minW={0}>
            <OwnerAvatar workspace={workspace} size={20} />
            <Text textStyle="phoneCaption" color="phone.muted" noOfLines={1}>
              {t('guest.header.compact', { name })}
            </Text>
          </Flex>
        )}
      />
      {step === 0 && (
        <Flex
          align="center"
          gap="12px"
          mt="16px"
          p="14px"
          borderWidth="1px"
          borderColor="redesign.border"
          borderRadius="16px"
          bg="redesign.bg.surface"
        >
          <OwnerAvatar workspace={workspace} size={44} />
          <Box minW={0}>
            <Text fontSize="16px" lineHeight="22px" fontWeight={700} color="redesign.heading">
              {title}
            </Text>
            <Text fontSize="13px" lineHeight="18px" color="phone.muted" mt="2px">{subtitle}</Text>
            <RoleChip role={role} mt="8px" />
          </Box>
        </Flex>
      )}
    </>
  );

  const renderWelcome = () => (
    <>
      {/* The invitation, already accepted: drawn like the owner flow's
          "account created" row. */}
      <Flex
        align="center"
        gap="14px"
        p="14px"
        mb="24px"
        borderWidth="1px"
        borderColor="redesign.border"
        borderRadius="16px"
        bg="redesign.bg.surface"
      >
        <Flex
          boxSize="40px"
          borderRadius="12px"
          align="center"
          justify="center"
          flexShrink={0}
          bg="rgba(21, 128, 61, 0.10)"
          _dark={{ bg: 'rgba(74, 222, 128, 0.14)' }}
          color="redesign.amount.income"
        >
          <Icon as={LuCheck} boxSize="20px" strokeWidth={2.2} />
        </Flex>
        <Box minW={0}>
          <Text fontSize={isPhone ? '16px' : '14px'} fontWeight={600} color="redesign.heading">
            {t('guest.welcome.acceptedTitle', { name })}
          </Text>
          <Text fontSize="13px" color={isPhone ? 'phone.muted' : 'redesign.text.muted'} lineHeight="1.4">
            {t('guest.welcome.acceptedBody', { role: roleLabel })}
          </Text>
        </Box>
      </Flex>

      <LanguageField onSelect={setLanguage} />
      {/* Not "(optional)": nothing on this page is required, and the note
          below says what the country is for. */}
      <CountryField value={country || suggested} onChange={chooseCountry} />

      <Text fontSize="13px" color={isPhone ? 'phone.muted' : 'redesign.text.muted'} lineHeight="1.5" mt="-4px" maxW="480px">
        {t(!country && suggested ? 'guest.welcome.countryNoteSuggested' : 'guest.welcome.countryNote', { name })}
      </Text>
    </>
  );

  const renderBody = () => {
    switch (key) {
      case 'welcome':
        return renderWelcome();
      case 'role':
        return <GuestRoleStep role={role} roleLabel={roleLabel} name={name} />;
      case 'done':
        return (
          <GuestFinish
            workspace={workspace}
            role={role}
            roleLabel={roleLabel}
            name={name}
            entering={entering}
            onEnter={enter}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  return (
    <OnboardingFrame
      steps={steps}
      step={step}
      header={header}
      phoneTop={phoneTop}
      desc={DESC_KEYS[key] ? t(DESC_KEYS[key], { name }) : null}
      onBack={handleBack}
      primary={{ label: t('wizard.continue'), onClick: handleNext }}
      hostName={name}
    >
      {renderBody()}
    </OnboardingFrame>
  );
}
